import { Injector, Injectable } from '@angular/core';
import { BaseHttpService } from './base-http.service';
import { HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AEntity } from '../models/a-entity';
import { EnmFilterType } from '../components/column-filter/column-filter.component';

/** Manage the main CRUD method to a given http route */
export abstract class CrudService<T extends AEntity> extends BaseHttpService {
    protected baseUrl: string = null;

    /**
     * Create a CRUD service to manage @param baseUrl data
     * @param baseUrl - relative root url api to access to the resource
     * @param injector - injector root service used in app.module
     */
    constructor(baseUrl: string, _injector: Injector, private _type: new () => T) {
        super(_injector);
        this.baseUrl = baseUrl;
    }
    
    /**
     * Get all records
     */
    public readAll(filter: string = null): Observable<T[]> {
        let request: HttpRequest<any> = this.getRequest(this.baseUrl, "GET");

        if (filter) request = request.clone({ url:request.url + '?' + filter });

        return this.executeQuery<T[]>(request)
            .pipe(
                map(json => this.convertToT(this._type, json) as T[])
            );
    }

    /**
     * Get a specific record
     * @param id - Id of the specific record
     */
    public readOne(id: number): Observable<T> {
        let request: HttpRequest<any> = this.getRequest(this.baseUrl, "GET", id);
        return this.executeQuery<T>(request)
            .pipe(
                map(json => this.convertToT(this._type, json) as T)
            );
    }

    /**
     * Store a new record
     * @param model - New record to store
     */
    public createOne(model: T): Observable<T> {
        let request: HttpRequest<any> = this.getRequest(this.baseUrl, "POST", undefined, model);
        return this.executeQuery<T>(request)
            .pipe(
                map(json => this.convertToT(this._type, json) as T)
            );
    }

    /**
     * Update an existing model in the store
     * @param model - Existing model to update according to its id
     */
    public updateOne(model: T): Observable<T> {
        let request: HttpRequest<any> = this.getRequest(this.baseUrl, "PUT", model.id, model);
        return this.executeQuery<T>(request)
            .pipe(
                map(json => this.convertToT(this._type, json) as T)
            );
    }

    /**
     * Update partially an existing model in the store with provided properties
     * @param model - Existing model to update according to its id
     */
    public partialUpdateOne(id: number, partialModel: any): Observable<T> {
        let request: HttpRequest<any> = this.getRequest(this.baseUrl, "PATCH", id, partialModel);
        return this.executeQuery<T>(request)
            .pipe(
                map(json => this.convertToT(this._type, json) as T)
            );
    }

    /**
     * Delete an existing model from the store
     * @param id - Id of an existing record
     */
    public deleteOne(id: number): Observable<T> {
        let request: HttpRequest<any> = this.getRequest(this.baseUrl, "DELETE", id);
        return this.executeQuery<T>(request)
            .pipe(
                map(json => this.convertToT(this._type, json) as T)
            );
    }
}
